import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { TimesheetFlowCard } from "@/features/timesheetFlow/components/TimesheetFlowCard";
import { TimesheetReminderMailDialog } from "@/features/timesheetFlow/components/TimesheetReminderMailDialog";
import {
  getDefaultPrintLocation,
  getMailSuggestion,
  getPublicLinks,
  listPrintLocations,
  setDefaultPrintLocation,
  type PrintLocation,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  getContactAgeBadgeConfig,
  resolveTimesheetMailPdfUrl,
} from "@/pages/stundennachweise/timesheetFilterLogic";
import { PrintJobFeedbackError } from "@/lib/printJob";

export function TimesheetExportActions({
  timesheetId,
  timesheet,
  displayNumber,
  busy,
  onPrint,
  onSendMail,
  onMailSent,
  onError,
}: {
  timesheetId: number;
  timesheet: TimesheetRecord;
  displayNumber: string;
  busy?: boolean;
  onPrint: (locationId: number, copies: number) => Promise<void>;
  onSendMail: (payload: {
    recipients: string[];
    cc?: string[];
    subject: string;
    message: string;
    pdfUrl: string;
    htmlBody?: string;
    reminder?: boolean;
    markContact?: boolean;
  }) => Promise<void>;
  onMailSent?: () => void | Promise<void>;
  onError?: (message: string) => void;
}) {
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [locationId, setLocationId] = useState("");
  const [copies, setCopies] = useState("1");
  const [printBusy, setPrintBusy] = useState(false);

  const [mailTo, setMailTo] = useState("");
  const [mailCc, setMailCc] = useState("");
  const [mailBusy, setMailBusy] = useState(false);
  const [mailLoaded, setMailLoaded] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  const contactAgeBadge = useMemo(
    () =>
      getContactAgeBadgeConfig({
        at_contact: Boolean(timesheet.at_contact),
        at_contact_at:
          typeof timesheet.at_contact_at === "string" ? timesheet.at_contact_at : null,
        status: timesheet.status,
      }),
    [timesheet.at_contact, timesheet.at_contact_at, timesheet.status],
  );

  const showReminderMail = Boolean(contactAgeBadge);

  useEffect(() => {
    void (async () => {
      try {
        const [locs, def] = await Promise.all([listPrintLocations(), getDefaultPrintLocation()]);
        setLocations(locs);
        if (def?.default_location_id) {
          setLocationId(String(def.default_location_id));
        } else if (locs[0]?.id) {
          setLocationId(String(locs[0].id));
        }
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  useEffect(() => {
    setMailLoaded(false);
    setMailTo("");
    setMailCc("");
  }, [timesheetId]);

  useEffect(() => {
    if (mailLoaded) return;
    void (async () => {
      try {
        const suggestion = await getMailSuggestion(timesheetId);
        const recipients = (suggestion.recipients || []).join(", ");
        if (recipients) setMailTo(recipients);
        setMailCc((suggestion.cc || []).join(", "));
      } catch {
        /* Empfänger bleibt leer */
      } finally {
        setMailLoaded(true);
      }
    })();
  }, [timesheetId, displayNumber, mailLoaded]);

  const resolvePdfUrl = async (options?: { reminder?: boolean }): Promise<string> => {
    const links = await getPublicLinks(timesheetId);
    return resolveTimesheetMailPdfUrl(timesheet, links, options);
  };

  const runPrint = async () => {
    const loc = Number(locationId);
    const numCopies = Math.max(1, Number(copies) || 1);
    if (!loc) {
      onError?.("Bitte einen Standort auswählen.");
      return;
    }
    setPrintBusy(true);
    try {
      await setDefaultPrintLocation(loc);
      await onPrint(loc, numCopies);
    } catch (err: unknown) {
      if (err instanceof PrintJobFeedbackError) {
        onError?.(err.feedback.message || err.feedback.title);
      } else {
        onError?.(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
      }
    } finally {
      setPrintBusy(false);
    }
  };

  const runSendMail = async (options: { reminder?: boolean }) => {
    const recipients = mailTo.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    const cc = mailCc.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      onError?.("Bitte mindestens einen Empfänger angeben.");
      return;
    }
    const isReminder = Boolean(options.reminder);
    const subject = isReminder
      ? `Erinnerung: Stundennachweis ${displayNumber}`
      : `Stundennachweis ${displayNumber}`;
    setMailBusy(true);
    try {
      const pdfUrl = await resolvePdfUrl({ reminder: isReminder });
      await onSendMail({
        recipients,
        cc,
        subject,
        message: "",
        pdfUrl,
        reminder: isReminder,
        markContact: !isReminder,
      });
    } catch (err: unknown) {
      onError?.(err instanceof Error ? err.message : "E-Mail konnte nicht versendet werden.");
    } finally {
      setMailBusy(false);
    }
  };

  const disabled = busy || printBusy || mailBusy;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <TimesheetFlowCard title="Drucken">
        <div className="eh-po-print-grid">
          <NativeSelectField
            label="Standort"
            selectSize="sm"
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
          >
            {locations.length === 0 ? (
              <option value="">— Standort auswählen —</option>
            ) : null}
            {locations.map((loc) => (
              <option key={loc.id} value={String(loc.id)}>
                {loc.name || `Standort ${loc.id}`}
              </option>
            ))}
          </NativeSelectField>
          <TextInput
            label="Kopien"
            type="number"
            min={1}
            inputClassName="!w-full"
            className="mb-0"
            value={copies}
            onChange={(e) => setCopies(e.target.value)}
          />
          <Button type="button" size="sm" disabled={disabled || !locationId} onClick={() => void runPrint()}>
            Drucken
          </Button>
        </div>
        <p className="mt-2 text-xs text-label-tertiary">
          Druckt die PDF am ausgewählten Standort und prüft den CUPS-Status (Papier, Toner, Warteschlange).
        </p>
      </TimesheetFlowCard>

      <TimesheetFlowCard title="E-Mail">
        <TextareaInput
          label="Empfänger"
          rows={2}
          value={mailTo}
          onChange={(e) => setMailTo(e.target.value)}
          placeholder="name@firma.de, weitere@firma.de"
        />
        <TextareaInput
          label="CC"
          rows={2}
          className="mt-2"
          value={mailCc}
          onChange={(e) => setMailCc(e.target.value)}
          placeholder="optional"
        />
        <p className="mt-1 text-xs text-label-tertiary">
          Mehrere Adressen mit Komma trennen. Die Team-Mail erscheint in CC, wenn sie für
          Stundennachweise hinterlegt ist.
        </p>

        {showReminderMail && contactAgeBadge ? (
          <p className="mt-3 text-xs text-label-secondary">
            Kontakt älter als {contactAgeBadge.label.replace("> ", "")} — Erinnerungsmail möglich, ohne den
            NW-Zähler zurückzusetzen.
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" size="sm" disabled={disabled} onClick={() => void runSendMail({ reminder: false })}>
            E-Mail senden
          </Button>
          {showReminderMail ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={disabled}
              onClick={() => {
                const recipients = mailTo.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
                if (!recipients.length) {
                  onError?.("Bitte mindestens einen Empfänger angeben.");
                  return;
                }
                setReminderDialogOpen(true);
              }}
            >
              Erinnerungsmail senden
            </Button>
          ) : null}
        </div>
      </TimesheetFlowCard>

      <TimesheetReminderMailDialog
        open={reminderDialogOpen}
        timesheetId={timesheetId}
        timesheet={timesheet}
        displayNumber={displayNumber}
        recipients={mailTo}
        cc={mailCc}
        onClose={() => setReminderDialogOpen(false)}
        onSent={async () => {
          await onMailSent?.();
        }}
        onError={onError}
      />
    </div>
  );
}